import type { IGlobalSettings } from './types'

export const getJamsBackendApi = () => {
  return import.meta.env.VITE_JAMS_BACKEND_URL
}

export const getCDNUrl = () => {
  return import.meta.env.VITE_CDN_URL
}

export const getChromeDataApi = () => {
  return import.meta.env.VITE_CHROME_DATA_API
}

export const getImageServiceUrl = () => {
  return import.meta.env.VITE_IMAGE_SERVICE_API
}

// Get account ID from URL or DOM attributes
export function getAccountIdFromUrlOrDom(): string {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const accountIdFromUrl = urlParams.get('account_id')

  if (accountIdFromUrl && accountIdFromUrl !== '') return accountIdFromUrl

  // Check DOM attributes
  const wpElement = document.getElementsByClassName('lightning-model-page')[0]
  const accountIdFromElement = wpElement?.getAttribute('data-account-id')

  if (accountIdFromElement && accountIdFromElement !== '')
    return accountIdFromElement

  return ''
}

// Get default global settings
export const getDefaultGlobalSettings = (): IGlobalSettings => {
  return {
    header: {
      modelName: {
        mobile: {
          fontSize: '14',
          fontSizeUnit: 'px',
        },
        desktop: {
          fontSize: '14',
          fontSizeUnit: 'px',
        },
      },
      price: {
        mobile: {
          fontSize: '14',
          fontSizeUnit: 'px',
        },
        desktop: {
          fontSize: '14',
          fontSizeUnit: 'px',
        },
      },
      trimStyle: {
        mobile: {
          fontSize: '12',
          fontSizeUnit: 'px',
        },
        desktop: {
          fontSize: '12',
          fontSizeUnit: 'px',
        },
      },
    },
    primaryCTA: {
      label: 'View Inventory',
      link: '',
      colors: {
        color: '#FFFFFF',
        background: '#0000FF',
        colorHover: '#FFFFFF',
        backgroundHover: '#0000FF',
      },
      icon: {
        colors: {
          color: '#000000',
          colorHover: '#000000',
          background: 'transparent',
          backgroundHover: 'transparent',
        },
        sizes: {
          container: '30',
          containerUnit: 'px',
          icon: '30',
          iconUnit: 'px',
        },
      },
      border: {
        width: '1',
        widthUnit: 'px',
        style: 'solid',
        color: '#000000',
        hoverColor: '#000000',
        radius: '0',
        radiusUnit: 'px',
      },
      typography: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '14',
        fontSizeUnit: 'px',
        fontStyle: 'normal',
        fontWeight: 'normal',
        letterSpacing: '0',
        letterSpacingUnit: 'px',
        lineHeight: '1.5',
        lineHeightUnit: 'em',
        textDecoration: 'none',
        textTransform: 'none',
        textAlignment: 'left',
      },
    },
    secondaryCTA: {
      label: 'Order Now',
      link: '',
      colors: {
        color: '#000000',
        background: 'transparent',
        colorHover: '#000000',
        backgroundHover: 'transparent',
      },
      icon: {
        colors: {
          color: '#000000',
          colorHover: '#000000',
          background: 'transparent',
          backgroundHover: 'transparent',
        },
        sizes: {
          container: '30',
          containerUnit: 'px',
          icon: '30',
          iconUnit: 'px',
        },
      },
      border: {
        width: '1',
        widthUnit: 'px',
        style: 'solid',
        color: '#000000',
        hoverColor: '#000000',
        radius: '0',
        radiusUnit: 'px',
      },
      typography: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '14',
        fontSizeUnit: 'px',
        fontStyle: 'normal',
        fontWeight: 'normal',
        letterSpacing: '0',
        letterSpacingUnit: 'px',
        lineHeight: '1.5',
        lineHeightUnit: 'em',
        textDecoration: 'none',
        textTransform: 'none',
        textAlignment: 'left',
      },
    },
    trimCard: {
      width: '340',
      widthUnit: 'px',
      height: '380',
      heightUnit: 'px',
      imageHeight: '200',
      imageHeightUnit: 'px',
      margin: '4',
      marginUnit: 'px',
      borderRadius: '0',
      borderRadiusUnit: 'px',
    },
  }
}

// Format currency
export const formatCurrency = (value: number): string => {
  if (value === 0) {
    return 'Call for Price'
  }

  return `$${value.toLocaleString('en-US')}`
}

// Convert string to PascalCase
export const toPascalCase = (str: string): string => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

// Get year from URL or other source
export const getYear = () => {
  // function body here
}

// Get make from URL or other source
export const getMake = () => {
  // function body here
}

// Get model from URL or other source
export const getModel = () => {
  // function body here
}

export const getImageAsync = (
  styleId: string,
  make: string,
  model: string,
  trim: string,
  color: string = '',
  size: string = 'med',
): Promise<string | null> => {
  const imageServiceUrl = getImageServiceUrl()
  const normalizedColor = color.replace('#', '').toLowerCase()
  const category = 'front34'
  const imageUrl = `${imageServiceUrl}/image?color=${normalizedColor}&styleId=${styleId}&make=${make}&model=${model}&trim=${trim}&category=${category}&size=${size}`

  return new Promise((resolve, reject) => {
    fetch(imageUrl)
      .then(response => {
        const isPlaceholder = response.status === 203

        if (response.ok && isPlaceholder) {
          resolve(null)
        } else {
          // Preload image
          const img = new Image()
          img.src = imageUrl

          resolve(imageUrl)
        }
      })
      .catch(() => {
        resolve(null)
      })
  })
}

export const getAnyImageForTrim = (trimData: any): string => {
  const imageServiceUrl = getImageServiceUrl()

  const styleId = trimData?.styleId ?? ''
  const year = trimData?.year ?? ''
  const make = trimData?.make ?? ''
  const model = trimData?.model ?? ''
  const trim = trimData?.trim ?? ''
  const size = 'med'

  return `${imageServiceUrl}/image?year=${year}&make=${make}&model=${model}&trim=${trim}&size=${size}&styleId=${styleId}`
}

export const getTrimColorizedImage = (
  trim: any,
  colorHex: string,
): string | null => {
  const colorizedImages = trim?.images?.colorized ?? null

  // Early returns for missing data
  if (
    !colorizedImages ||
    (Array.isArray(colorizedImages) && colorizedImages.length === 0)
  ) {
    return null
  }

  // Find the image that matches the color
  const imageForColor = trim.images.colorized.find(
    (image: any) => image.colorValue === colorHex,
  )

  // If no image found, return null
  if (!imageForColor?.modes) return null

  // Find exterior mode images
  const exteriorImages = imageForColor.modes.find(
    (modeItem: any) => modeItem.mode === 'EXTERIOR',
  )

  // Return the first image or null
  return exteriorImages?.items?.[0]?.img ?? null
}

export const getSizeAndUnitCSS = (key: string, data: any) => {
  const unitKey = `${key}Unit`
  return `${data[key]}${data[unitKey]}`
}

export const getBorderCSS = (data: {
  width: string | number
  widthUnit: string
  style: string
  color: string
}) => {
  return `${data.width}${data.widthUnit} ${data.style} ${data.color}`
}

// Logger helper
export const log = (
  type: 'log' | 'info' | 'warn' | 'error' | 'debug',
  message: string,
  data?: any,
) => {
  console?.[type](`Lightning Model Pages - ${message}`, data || '')
}
